import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '_constants/theme';
const { devices } = theme;
import { luminance } from '_helpers/theme';

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledGroup = styled(forwardRef(({ borderColor, hoverColor, hoverTextColor, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  align-items: center;
  gap: 10px;
  background: ${({ color }) => color};
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: 16px;
  flex: none;
  order: 0;
  flex-grow: 0;
  padding-left: 10px;
  padding-right: 10px;

  .prefixInput,
  .suffixInput {
    display: inline-flex;
    align-items: center;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  ${({ disabled, theme }) =>
    disabled &&
    css`
      border-color: ${theme.colors.gray[500]};
      background: ${theme.colors.gray[500]};
      color: ${theme.colors.gray[400]};

      svg {
        path {
          fill: ${theme.colors.gray[400]};
        }
      }
    `}

  &:hover {
    ${({ disabled, theme, hoverColor, hoverTextColor }) =>
      !disabled &&
      css`
        outline-offset: 1px;
        outline: ${({ theme }) => theme.colors.blue[100]} solid 4px;
        border-color: ${({ color }) => hoverColor || luminance(color, -0.1)};
        background: ${({ color }) => hoverColor || luminance(color, -0.1)};
        color: ${hoverTextColor || theme.colors.white};
      `}
  }
  @media ${devices.tablet} {
    border-radius: 10px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledInput = styled(forwardRef(({ textColor, hoverColor, hoverTextColor, ...props }, ref) => <input ref={ref} {...props} />))`
  font-family: 'Aeonik';
  font-style: normal;
  font-size: 16px;
  font-weight: 400;
  flex-grow: 1;
  border: none;
  outline: none;
  background: none;
  padding-top: 10px;
  padding-bottom: 10px;
  width: ${({ width }) => width};
  color: ${({ textColor }) => textColor};

  ${({ disabled, theme }) =>
    disabled &&
    css`
      border-color: ${theme.colors.gray[500]};
      background: ${theme.colors.gray[500]};
      color: ${theme.colors.gray[400]};

      svg {
        path {
          fill: ${theme.colors.gray[400]};
        }
      }
    `}

  &:hover {
    ${({ disabled, hoverColor }) =>
      !disabled &&
      css`
        background: ${({ color }) => hoverColor || luminance(color, -0.1)};
      `}
  }
`;

const Input = ({
  color,
  borderColor,
  textColor,
  hoverColor,
  hoverTextColor,
  disabled,
  prefix,
  suffix,
  value,
  onChange,
  onKeyPress,
  width,
  ...restProps
}) => {
  return (
    <StyledGroup
      color={color}
      borderColor={borderColor}
      disabled={disabled}
      hoverColor={hoverColor}
      hoverTextColor={hoverTextColor}
    >
      {prefix && <span className="prefixInput">{prefix}</span>}
      <StyledInput
        disabled={disabled}
        {...restProps}
        color={color}
        textColor={textColor}
        hoverColor={hoverColor}
        hoverTextColor={hoverTextColor}
        width={width}
        value={value}
        onChange={onChange}
        onKeyPress={onKeyPress}
        data-hj-allow
      />
      {suffix && <span className="suffixInput">{suffix}</span>}
    </StyledGroup>
  );
};

Input.propTypes = {
  borderColor: PropTypes.string,
  color: PropTypes.string,
  textColor: PropTypes.string,
  hoverColor: PropTypes.string,
  hoverTextColor: PropTypes.string,
  disabled: PropTypes.bool,
  large: PropTypes.bool,
  outlined: PropTypes.bool,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
};

export default Input;
